import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { message } from '@client/components/_ui/feedback/message';
import { confirmAction, confirmDestructive } from '@client/components/_ui/feedback/modal';
import { Space } from '@client/components/_ui/layout/Space';
import { FlowView } from '@client/components/dynamic-form/visual-builder/flow-view/FlowView';
import {
    type FormHealthTarget,
    resolveFormHealth,
} from '@client/components/dynamic-form/visual-builder/form-health/resolve-form-health';
import { useBuilderValidation } from '@client/components/dynamic-form/visual-builder/hooks/use-builder-validation';
import { LogicOverview } from '@client/components/dynamic-form/visual-builder/logic-view/LogicOverview';
import { SAMPLE_SCHEMA } from '@client/components/dynamic-form/visual-builder/sample-data/insurance-claim-sample-schema';
import { type BuildInspectorTarget, VisualBuilder } from '@client/components/dynamic-form/visual-builder/VisualBuilder';
import { refreshFormDefinitionOnAutosaveConflict } from '@client/components/form-designer/autosave-conflict';
import { type CompareTarget, CompareVersionsDialog } from '@client/components/form-designer/CompareVersionsDialog';
import { FormDependentsProvider } from '@client/components/form-designer/dependents';
import { type AutosaveIndicator, EditorHeader } from '@client/components/form-designer/EditorHeader';
import { type EditorView, isEditorView } from '@client/components/form-designer/editor-views';
import { resolveNewDefinitionIdentity } from '@client/components/form-designer/form-definition-identity';
import { HistoryDrawer } from '@client/components/form-designer/HistoryDrawer';
import { JsonSourceView } from '@client/components/form-designer/JsonSourceView';
import { PreviewDrawer } from '@client/components/form-designer/PreviewDrawer';
import { PublishDialog, type PublishTarget } from '@client/components/form-designer/PublishDialog';
import { SettingsView } from '@client/components/form-designer/SettingsView';
import {
    type DefinitionDetailsFormValues,
    normaliseTags,
    useDefinitionDetailsFormSync,
} from '@client/components/form-designer/useDefinitionDetailsFormSync';
import { useFormDefinitionMutations } from '@client/components/form-designer/useFormDefinitionMutations';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useFormDesignerSchema } from '@client/hooks/form-designer/useFormDesignerSchema';
import { usePreviewController, usePreviewNavigation } from '@client/hooks/form-designer/usePreviewController';
import { useSerialAutosave } from '@client/hooks/form-designer/useSerialAutosave';
import { useUnsavedChangesPrompt } from '@client/hooks/forms/useUnsavedChangesPrompt';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { type RouterInputs, trpc } from '@client/trpc/client';
import type { DehydratedBuilderSchema } from '@shared/schemas/dynamic-forms/builder-schema';
import { normaliseBuilderConditions } from '@shared/schemas/dynamic-forms/condition-normaliser';
import type { DehydratedFormSchema } from '@shared/schemas/dynamic-forms/schema-normaliser';
import { skipToken } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Alert, Form } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from 'zustand';

type BuilderDocumentInput = NonNullable<RouterInputs['formDesigner']['createFormDefinition']['builderDocument']>;

type FormDesignerSearch = {
    view?: EditorView;
};

export const Route = createFileRoute('/console/$orgId/configuration/claims-products/forms/$formDefinitionId/')({
    staticData: {
        // No `metadata.title` here on purpose: it would add a third, redundant
        // crumb ("Edit form") after the parent's, which already ends the trail
        // at the form's own name.
        ui: {
            // Full-bleed workspace (compliance-audit pattern): strip the page
            // canvas padding so the editor fills the content area edge to edge.
            className: 'p-0',
            heightVariant: 'fit',
            // The editor is a full-bleed workspace, not a page with a header. The parent
            // route's `useTitleForChildren` would otherwise stamp a "Form designer" title
            // chip above it — which the breadcrumb already says, and which the form's own
            // name repeats one row below.
            hideTitle: true,
        },
    },
    validateSearch: (search: Record<string, unknown>): FormDesignerSearch => ({
        view: isEditorView(search.view) ? search.view : undefined,
    }),
    component: FormDefinitionEditor,
});

const DEFAULT_SCHEMA: DehydratedBuilderSchema = {
    title: 'New form',
    description: '',
    nodes: [
        {
            nodeType: 'page',
            name: 'page-1',
            title: 'Page 1',
            elements: [
                {
                    type: 'text',
                    name: 'example-field',
                    title: 'Example field',
                },
            ],
        },
    ],
};

/**
 * The designer route: loads the definition, owns the editable model and the
 * autosave, and composes the header, the views and the drawers from
 * `components/form-designer`.
 */
function FormDefinitionEditor() {
    const { orgId, formDefinitionId } = Route.useParams();
    const { view: viewParam } = Route.useSearch();
    const navigate = useNavigate();
    const { trpcUtils } = useTrpc();
    const { formatDate } = useFormatter();
    const isNew = formDefinitionId === 'new';

    const view: EditorView = viewParam ?? 'build';
    const viewRef = useRef(view);
    viewRef.current = view;

    const preview = usePreviewController();
    const [historyOpen, setHistoryOpen] = useState(false);
    const [saveState, setSaveState] = useState<'idle' | 'saved' | 'error'>('idle');
    const [buildInspectorTarget, setBuildInspectorTarget] = useState<BuildInspectorTarget>();
    /** Bumped when something asks the header to open its title editor. */
    const [renameRequestId, setRenameRequestId] = useState(0);
    /** The version the publish dialog is open for. */
    const [publishTarget, setPublishTarget] = useState<PublishTarget>();
    /** The version being compared with the draft. */
    const [compareTarget, setCompareTarget] = useState<CompareTarget>();

    // Fetch existing definition
    const {
        data: definition,
        isLoading,
        error: definitionError,
    } = trpc.formDesigner.getFormDefinition.useQuery(
        isNew ? skipToken : { formDefinitionId: BigInt(formDefinitionId) },
    );

    const latestVersion = definition?.Versions[0];
    // Who uses the form — read-only, from the consumer registry. Feeds the header
    // chip and the inspector's Data tab; the publish dialog asks for its own impact.
    const { data: dependentsData } = trpc.formDesigner.listDependents.useQuery(
        isNew ? skipToken : { formDefinitionId: BigInt(formDefinitionId) },
        { meta: { errorSurface: 'inline' } },
    );
    const dependents = dependentsData?.dependents;
    const {
        data: versionHistory,
        hasNextPage: hasMoreVersions,
        fetchNextPage: fetchMoreVersions,
        isFetchingNextPage: isFetchingMoreVersions,
        isFetchNextPageError,
        isLoading: isLoadingVersionHistory,
        error: versionHistoryError,
        refetch: refetchVersionHistory,
    } = trpc.formDesigner.listVersionHistory.useInfiniteQuery(
        { formDefinitionId: isNew ? 0n : BigInt(formDefinitionId), limit: 20 },
        {
            enabled: historyOpen && !isNew,
            getNextPageParam: (lastPage) => lastPage.nextCursor,
        },
    );
    const versionHistoryRows = versionHistory?.pages.flatMap((page) => page.versions) ?? [];

    // Schema hook — the builder document is the one editable model; the runtime
    // schema is compiled from it for the preview, the JSON view and the save.
    const {
        hydrated,
        compiledJson,
        copied,
        formStore,
        builderStore,
        isDirty,
        hasRemoteUpdate,
        editVersion,
        importJson,
        renameFormTitle,
        copySchema,
        getSchemaForSave,
        onSaveSuccess: markSchemaSaved,
        initializeFromFetched,
        getExpectedSnapshotHash,
        markPersistedSnapshot,
        reloadRemoteSnapshot,
        keepLocalChanges,
    } = useFormDesignerSchema({
        formDefinitionId: isNew ? 'new' : formDefinitionId,
        version: latestVersion?.version ?? 1,
        initialSchema: isNew ? DEFAULT_SCHEMA : undefined,
        previewActive: preview.active,
        jsonActive: view === 'source',
    });

    // Guard against losing unsaved edits on navigation / refresh / tab close.
    // A ref (not state) so it can be cleared synchronously before the post-create
    // programmatic navigate, without waiting for a re-render.
    const isDirtyRef = useRef(isDirty);
    isDirtyRef.current = isDirty;
    useUnsavedChangesPrompt(isDirtyRef);

    // Builder-level validation — computed once here and passed into the views so
    // the schema is validated a single time per change. Gates the Publish action
    // client-side; the server re-validates authoritatively on publish, so this
    // is UX, not the security gate.
    const builderValidation = useBuilderValidation(builderStore);
    const schemaHasErrors = builderValidation.hasErrors;
    // The form title a respondent sees. The header shows this rather than the
    // internal definition name, so the editor and the rendered form never
    // disagree about what the form is called.
    const builderSchema = useStore(builderStore, (s) => s.schema);
    const schemaTitle = builderSchema.title;

    // Every problem, resolved to a location and a destination — the data behind
    // the header's health chip.
    const healthIssues = useMemo(
        () => resolveFormHealth(builderSchema, builderValidation.warnings),
        [builderSchema, builderValidation.warnings],
    );

    // Load the fetched version into the builder store. Versions saved before the
    // builder document was persisted arrive with only a runtime schema, which the
    // hook derives a document from.
    useEffect(() => {
        if (latestVersion && !isNew) {
            initializeFromFetched(
                {
                    builderDocument: latestVersion.builderDocument as DehydratedBuilderSchema | null,
                    runtimeSchema: latestVersion.runtimeSchema as DehydratedFormSchema,
                },
                latestVersion.version,
                latestVersion.snapshotHash,
            );
        }
    }, [latestVersion, isNew, initializeFromFetched]);

    const handleViewChange = useCallback(
        (newView: EditorView) => {
            if (viewRef.current === newView) return;
            // Cast mirrors SegmentedNavigation — generic navigation cannot infer
            // this route's search param types.
            void (navigate as (opts: Record<string, unknown>) => void)({
                search: (prev: Record<string, unknown>) => ({
                    ...prev,
                    view: newView === 'build' ? undefined : newView,
                }),
                replace: true,
            });
        },
        [navigate],
    );

    // Details form state
    const [detailsForm] = Form.useForm<DefinitionDetailsFormValues>();
    useDefinitionDetailsFormSync(detailsForm, definition);

    const { invalidateDefinition, create, saveDraft, updateDetails, publish, archive, restore } =
        useFormDefinitionMutations({
            orgId,
            formDefinitionId,
            detailsForm,
            onCreated: () => {
                markSchemaSaved();
                // Clear the dirty ref synchronously so the unsaved-changes blocker
                // does not prompt on the programmatic post-create navigate.
                isDirtyRef.current = false;
            },
        });

    // Autosave — silent draft persistence. The coordinator serialises requests,
    // retains edits made in flight, and latches failures until the next edit.
    // The saved result reaches the cache before invalidation so a remount cannot
    // re-seed from the pre-save snapshot if the subsequent refetch fails.
    const { isAutosaving } = useSerialAutosave({
        enabled: !isNew && latestVersion !== undefined,
        isDirty,
        editVersion,
        getSnapshot: () => {
            if (!latestVersion) return null;
            const document = getSchemaForSave();
            if (!document) return null;
            const expectedSnapshotHash = getExpectedSnapshotHash();
            if (!expectedSnapshotHash) return null;
            return {
                versionId: latestVersion.id,
                expectedSnapshotHash,
                builderDocument: document satisfies DehydratedBuilderSchema as BuilderDocumentInput,
            };
        },
        save: (snapshot) =>
            saveDraft.mutateAsync({
                formDefinitionId: BigInt(formDefinitionId),
                versionId: snapshot.versionId,
                expectedSnapshotHash: snapshot.expectedSnapshotHash,
                builderDocument: snapshot.builderDocument,
            }),
        onLatestSaveSuccess: () => {
            markSchemaSaved();
        },
        onSaveSuccess: (updated) => {
            markPersistedSnapshot(updated.snapshotHash, updated.version);
            setSaveState('saved');
            // Editing a published version silently forks a new draft server-side —
            // without this, the only trace is the small version badge changing.
            if (latestVersion && updated.version > latestVersion.version) {
                message.info(
                    `Draft v${updated.version} created — v${latestVersion.version} stays published until you publish again`,
                );
            }
            trpcUtils.formDesigner.getFormDefinition.setData({ formDefinitionId: BigInt(formDefinitionId) }, (prev) => {
                if (!prev) return prev;
                const exists = prev.Versions.some((version) => version.id === updated.id);
                return {
                    ...prev,
                    // saveDraft clones to a new version when the draft was
                    // published mid-edit — prepend it so it becomes the latest.
                    Versions: exists
                        ? prev.Versions.map((version) => (version.id === updated.id ? updated : version))
                        : [updated, ...prev.Versions],
                };
            });
            invalidateDefinition();
        },
        onSaveError: (error) => {
            setSaveState('error');
            void refreshFormDefinitionOnAutosaveConflict(error, () =>
                trpcUtils.formDesigner.getFormDefinition.invalidate({
                    formDefinitionId: BigInt(formDefinitionId),
                }),
            );
            errorMessage.show(error);
        },
    });

    // Create (new forms only — existing drafts autosave).
    const handleCreate = useCallback(() => {
        const document = getSchemaForSave();
        if (!document) {
            message.warning('Every page needs at least one field before the form can be created');
            return;
        }
        const values = detailsForm.getFieldsValue();
        const identity = resolveNewDefinitionIdentity({
            schemaKey: values.schemaKey ?? '',
            schemaTitle,
            defaultSchemaTitle: DEFAULT_SCHEMA.title,
            timestamp: Date.now(),
        });
        create.mutate({
            ...identity,
            description: values.description || undefined,
            builderDocument: document satisfies DehydratedBuilderSchema as BuilderDocumentInput,
        });
    }, [getSchemaForSave, detailsForm, create.mutate, schemaTitle]);

    const handleDetailsSubmit = useCallback(async () => {
        if (isNew) return;
        // validateFields, not getFieldsValue — otherwise the field-level rules
        // (required name, tag length) are decoration and the server does the
        // rejecting. A failed validation shows inline errors and stops here.
        let values: DefinitionDetailsFormValues;
        try {
            values = await detailsForm.validateFields();
        } catch {
            return;
        }
        updateDetails.mutate({
            formDefinitionId: BigInt(formDefinitionId),
            description: values.description || null,
            tags: normaliseTags(values.tags),
            // The key is the form's public identity; the server refuses a change
            // once a version has been published, and the field is disabled then.
            ...(definition?.schemaKeyLocked ? {} : { schemaKey: values.schemaKey }),
        });
    }, [isNew, detailsForm, updateDetails.mutate, formDefinitionId, definition?.schemaKeyLocked]);

    /**
     * The header renames the form — its title, the one name the respondent, the
     * list and the editor share. The definition record follows on the server
     * with the schema autosave, so there is nothing else to keep in step here.
     */
    const handleInlineRename = useCallback((title: string): boolean => renameFormTitle(title), [renameFormTitle]);

    /** Bring a published or archived version back as the draft, after one confirmation. */
    const confirmRestore = useCallback(
        (version: { id: bigint; version: number }) => {
            confirmAction({
                title: `Restore version ${version.version} as the draft?`,
                content:
                    latestVersion?.status === 'DRAFT'
                        ? `The current draft v${latestVersion.version} is replaced with the content of v${version.version}. Nothing is published.`
                        : `A new draft is created from v${version.version}. Nothing is published.`,
                okText: 'Restore',
                onOk: () => restore.mutate({ versionId: version.id }),
            });
        },
        [latestVersion, restore.mutate],
    );

    /** Send the user to whatever a health issue actually refers to. */
    const handleOpenHealthIssue = useCallback(
        (target: FormHealthTarget) => {
            switch (target.kind) {
                case 'element':
                    // The aspect the issue is about decides the inspector tab —
                    // a validator problem opening on Field shows no control that
                    // could fix it.
                    setBuildInspectorTarget({
                        panel: 'element-properties',
                        elementName: target.elementName,
                        tab: target.aspect ?? 'field',
                    });
                    handleViewChange('build');
                    builderStore.getState().setSelection({
                        type: 'element',
                        nodeName: target.nodeName,
                        elementName: target.elementName,
                    });
                    break;
                case 'page':
                    // Page properties, not the default Elements toolbox.
                    setBuildInspectorTarget({
                        panel: 'page-properties',
                        tab: target.aspect === 'logic' ? 'logic' : 'page',
                    });
                    handleViewChange('build');
                    builderStore.getState().setSelection({ type: 'page', nodeName: target.nodeName });
                    break;
                case 'form-title':
                    // The header owns the respondent-facing title — open its editor.
                    setRenameRequestId((id) => id + 1);
                    break;
                case 'json':
                    handleViewChange('source');
                    break;
                case 'navigation':
                    handleViewChange('flow');
                    if (target.nodeName !== undefined) {
                        builderStore.getState().setSelection({ type: 'page', nodeName: target.nodeName });
                    }
                    break;
                case 'automation':
                    handleViewChange('logic');
                    builderStore.getState().setAutomationPanelOpen(true);
                    break;
                case 'settings':
                    handleViewChange('settings');
                    break;
            }
        },
        [handleViewChange, builderStore],
    );

    // The preview's form store is created when the drawer opens, so the jump to a
    // requested start page waits for it.
    usePreviewNavigation(preview, formStore, builderStore);

    const applyLoadSample = useCallback(() => {
        // A user edit: the store subscription marks the draft dirty.
        builderStore.getState().setSchema(normaliseBuilderConditions(SAMPLE_SCHEMA));
        detailsForm.setFieldsValue({
            schemaKey: 'insurance-claim-intake-sample',
            description: 'Sample form demonstrating all element types, branching, computed fields, and automations.',
        });
    }, [builderStore, detailsForm]);

    const handleLoadSample = useCallback(() => {
        if (isDirty) {
            confirmDestructive({
                title: 'Replace current form?',
                content: 'Loading the sample will replace all current content. This cannot be undone.',
                okText: 'Replace',
                onOk: applyLoadSample,
            });
        } else {
            applyLoadSample();
        }
    }, [isDirty, applyLoadSample]);

    // Publishing opens a dialog that says what it does to the applications
    // using the form (see PublishDialog); the mutation runs from its confirm.
    const confirmPublish = useCallback((versionId: bigint, versionNumber: number) => {
        setPublishTarget({ versionId, versionNumber });
    }, []);
    const suggestionCount = useMemo(
        () => healthIssues.filter((issue) => issue.severity !== 'error').length,
        [healthIssues],
    );

    const publishBlockedReason = useMemo(() => {
        if (schemaHasErrors) return 'Resolve the validation issues before publishing';
        if (isDirty || isAutosaving) return 'Waiting for autosave to finish';
        return undefined;
    }, [schemaHasErrors, isDirty, isAutosaving]);
    const publishDisabledReason = useMemo(() => {
        if (!latestVersion) return 'No version is available to publish';
        if (latestVersion.status === 'PUBLISHED') return 'The latest version is already published';
        if (latestVersion.status === 'ARCHIVED') return 'Archived versions cannot be published';
        return publishBlockedReason;
    }, [latestVersion, publishBlockedReason]);

    const autosaveIndicator: AutosaveIndicator = isNew
        ? null
        : isAutosaving
          ? 'saving'
          : hasRemoteUpdate
            ? 'remote-update'
            : saveState === 'error'
              ? 'error'
              : saveState === 'saved' && !isDirty
                ? 'saved'
                : null;

    const jumpToBuild = useCallback(
        (target?: BuildInspectorTarget) => {
            setBuildInspectorTarget(target);
            handleViewChange('build');
        },
        [handleViewChange],
    );
    const jumpToFlow = useCallback(() => handleViewChange('flow'), [handleViewChange]);

    /** Flow → Build for one page: select it, then land on its page inspector. */
    const editPage = useCallback(
        (pageName: string) => {
            builderStore.getState().setSelection({ type: 'page', nodeName: pageName });
            setBuildInspectorTarget({ panel: 'page-properties', tab: 'page' });
            handleViewChange('build');
        },
        [builderStore, handleViewChange],
    );

    if (isLoading && !isNew) {
        return (
            <PageController route={Route}>
                <Loading size="large" className="py-16" />
            </PageController>
        );
    }

    // Surface a failed load instead of silently rendering the blank "new form"
    // defaults over an existing definition.
    if (definitionError && !isNew) {
        return (
            <PageController route={Route}>
                <Alert
                    type="error"
                    title="Failed to load form definition"
                    description={definitionError.message}
                    showIcon
                    className="m-4"
                />
            </PageController>
        );
    }

    return (
        <PageController route={Route}>
            <div className="flex h-full min-h-0 flex-col overflow-hidden bg-card">
                <EditorHeader
                    orgId={orgId}
                    isNew={isNew}
                    store={builderStore}
                    view={view}
                    onViewChange={handleViewChange}
                    schemaTitle={schemaTitle}
                    onRename={handleInlineRename}
                    renameRequestId={renameRequestId}
                    latestVersion={latestVersion}
                    dependents={dependents}
                    autosave={autosaveIndicator}
                    healthIssues={healthIssues}
                    onOpenHealthIssue={handleOpenHealthIssue}
                    onLoadSample={handleLoadSample}
                    onOpenHistory={() => setHistoryOpen(true)}
                    onOpenPreview={() => preview.openPreview()}
                    onCreate={handleCreate}
                    isCreating={create.isPending}
                    onPublish={() => latestVersion && confirmPublish(latestVersion.id, latestVersion.version)}
                    isPublishing={publish.isPending}
                    publishDisabledReason={publishDisabledReason}
                />

                {hasRemoteUpdate && (
                    <Alert
                        type="warning"
                        title="Another author saved a newer draft while you had unsaved changes."
                        description="Choose whether to keep your local edits or reload the latest saved draft. Autosave is paused until you decide."
                        action={
                            <Space size="small">
                                <Button
                                    size="small"
                                    type="primary"
                                    onClick={() => {
                                        keepLocalChanges();
                                        setSaveState('idle');
                                    }}
                                >
                                    Keep my edits
                                </Button>
                                <Button
                                    size="small"
                                    type="secondary"
                                    danger
                                    onClick={() => {
                                        reloadRemoteSnapshot();
                                        setSaveState('idle');
                                    }}
                                >
                                    Reload latest
                                </Button>
                            </Space>
                        }
                        className="shrink-0 rounded-none"
                        showIcon
                    />
                )}

                {/* Active view — fills the shell edge to edge */}
                <FormDependentsProvider dependents={dependents}>
                    <div className="min-h-0 flex-1">
                        {view === 'build' && (
                            <VisualBuilder
                                store={builderStore}
                                formDefinitionId={isNew ? 'new' : formDefinitionId}
                                version={latestVersion?.version ?? 1}
                                validation={builderValidation}
                                onOpenFlow={jumpToFlow}
                                onPreviewPage={preview.openPreview}
                                inspectorTarget={buildInspectorTarget}
                                onInspectorTargetHandled={() => setBuildInspectorTarget(undefined)}
                            />
                        )}
                        {view === 'flow' && (
                            <FlowView
                                store={builderStore}
                                validation={builderValidation}
                                onEditPage={editPage}
                                onPreviewPage={preview.openPreview}
                            />
                        )}
                        {view === 'logic' && (
                            <LogicOverview store={builderStore} onJumpToBuild={jumpToBuild} onJumpToFlow={jumpToFlow} />
                        )}
                        <SettingsView
                            activeTab={view === 'settings' ? 'details' : view === 'source' ? 'source' : null}
                            onTabChange={(tab) => handleViewChange(tab === 'source' ? 'source' : 'settings')}
                            form={detailsForm}
                            isNew={isNew}
                            definition={definition}
                            schemaTitle={schemaTitle}
                            onSave={handleDetailsSubmit}
                            source={
                                view === 'source' && (
                                    <JsonSourceView
                                        store={builderStore}
                                        formDefinitionId={isNew ? 'new' : formDefinitionId}
                                        version={latestVersion?.version ?? 1}
                                        compiledJson={compiledJson}
                                        copied={copied}
                                        onCopy={copySchema}
                                        isDirty={isDirty}
                                        importJson={importJson}
                                    />
                                )
                            }
                        />
                    </div>
                </FormDependentsProvider>
            </div>

            <PublishDialog
                target={publishTarget}
                formTitle={schemaTitle}
                suggestionCount={suggestionCount}
                isPublishing={publish.isPending}
                onCancel={() => setPublishTarget(undefined)}
                onConfirm={(input) =>
                    publish.mutate(input, {
                        onSuccess: () => setPublishTarget(undefined),
                    })
                }
            />

            <PreviewDrawer
                open={preview.open}
                onClose={preview.closePreview}
                parsedSchema={hydrated}
                formStore={formStore}
                onReset={preview.resetPreview}
                unreachablePageTitle={preview.unreachablePageTitle}
            />

            <HistoryDrawer
                open={historyOpen}
                onClose={() => setHistoryOpen(false)}
                formDefinitionId={formDefinitionId}
                isNew={isNew}
                hasDefinition={definition !== undefined}
                versions={versionHistoryRows}
                isLoadingVersions={isLoadingVersionHistory}
                versionsError={versionHistoryError}
                isFetchNextPageError={isFetchNextPageError}
                hasMoreVersions={hasMoreVersions}
                isFetchingMoreVersions={isFetchingMoreVersions}
                onRetryVersions={() => void refetchVersionHistory()}
                onLoadMoreVersions={() => void fetchMoreVersions()}
                formatDate={formatDate}
                isPublishing={publish.isPending}
                publishBlockedReason={publishBlockedReason}
                onPublish={confirmPublish}
                onArchive={(versionId) => archive.mutate({ versionId, reason: 'Manually archived' })}
                draft={
                    latestVersion?.status === 'DRAFT'
                        ? { id: latestVersion.id, version: latestVersion.version }
                        : undefined
                }
                onCompare={(from) =>
                    latestVersion &&
                    setCompareTarget({
                        fromVersionId: from.id,
                        fromVersion: from.version,
                        toVersionId: latestVersion.id,
                        toVersion: latestVersion.version,
                    })
                }
                onRestore={confirmRestore}
            />

            <CompareVersionsDialog target={compareTarget} onClose={() => setCompareTarget(undefined)} />
        </PageController>
    );
}
